import { fromJS } from 'immutable';
import { createSelector } from 'reselect';

import { CreateFeedback, CreateRating, CreateSupportCase, GetRating } from '../../api/Feedback';

// ACTIONS TYPES
export const CREATING_FEEDBACK = 'customer-portal/feedback/CREATING_FEEDBACK';
export const CREATE_FEEDBACK_ERROR = 'customer-portal/feedback/CREATING_FEEDBACK_ERROR';
export const CREATE_FEEDBACK_SUCCESS = 'customer-portal/feedback/CREATING_FEEDBACK_SUCCESS';
export const CREATING_SUPPORT_CASE = 'customer-portal/feedback/CREATING_SUPPORT_CASE';
export const CREATE_SUPPORT_CASE_ERROR = 'customer-portal/feedback/CREATING_SUPPORT_CASE_ERROR';
export const CREATE_SUPPORT_CASE_SUCCESS = 'customer-portal/feedback/CREATING_SUPPORT_CASE_SUCCESS';
export const CREATING_RATING = 'customer-portal/feedback/CREATING_RATING';
export const CREATE_RATING_ERROR = 'customer-portal/feedback/CREATE_RATING_ERROR';
export const CREATE_RATING_SUCCESS = 'customer-portal/feedback/CREATE_RATING_SUCCESS';
export const FETCHING_RATING = 'customer-portal/feedback/FETCHING_RATING';
export const FETCH_RATING_ERROR = 'customer-portal/feedback/FETCH_RATING_ERROR';
export const FETCH_RATING_SUCCESS = 'customer-portal/feedback/FETCH_RATING_SUCCESS';

// ACTION CREATORS
export const createSupportCaseSuccess = () => ({
  type: CREATE_SUPPORT_CASE_SUCCESS,
});

export const createSupportCaseError = err => ({
  type: CREATE_SUPPORT_CASE_ERROR,
  payload: err,
});

export const creatingSupportCase = () => ({
  type: CREATING_SUPPORT_CASE,
});

export const createFeedbackSuccess = () => ({
  type: CREATE_FEEDBACK_SUCCESS,
});

export const createFeedbackError = err => ({
  type: CREATE_FEEDBACK_ERROR,
  payload: err,
});

export const creatingFeedback = () => ({
  type: CREATING_FEEDBACK,
});

export const createRatingSuccess = number => ({
  type: CREATE_RATING_SUCCESS,
  payload: number,
});

export const createRatingError = err => ({
  type: CREATE_RATING_ERROR,
  payload: err,
});

export const creatingRating = () => ({
  type: CREATING_RATING,
});

export const fetchRatingSuccess = number => ({
  type: FETCH_RATING_SUCCESS,
  payload: number,
});

export const fetchRatingFailure = err => ({
  type: FETCH_RATING_ERROR,
  payload: err,
});

export const fetchingRating = () => ({
  type: FETCHING_RATING,
});

// THUNKS
export const createFeedback = (contactId, feedback, rating, faqTitle) => async dispatch => {
  dispatch(creatingFeedback());
  try {
    await CreateFeedback(contactId, feedback, rating, faqTitle);
    dispatch(createFeedbackSuccess());
  } catch (e) {
    dispatch(createFeedbackError(e));
  }
};

export const createRating = (contactId, rating) => async dispatch => {
  dispatch(creatingRating());
  try {
    await CreateRating(contactId, rating);
    dispatch(createRatingSuccess(rating));
  } catch (e) {
    dispatch(createRatingError(e));
  }
};

export const createSupportCase = (prospectId, emailBody) => async dispatch => {
  dispatch(creatingSupportCase());
  try {
    await CreateSupportCase(prospectId, emailBody);
    dispatch(createSupportCaseSuccess());
  } catch (e) {
    dispatch(createSupportCaseError('Error sending message.'));
  }
};

export const fetchRating = contactId => async dispatch => {
  dispatch(fetchingRating());
  try {
    const rating = await GetRating(contactId);
    dispatch(fetchRatingSuccess(rating));
  } catch (error) {
    dispatch(fetchRatingFailure(error));
  }
};

export const shouldFetchRating = ratingState => !ratingState.get('loaded') && !ratingState.get('loading');

export const fetchRatingIfNeeded = contactId => (dispatch, getState) => {
  if (!shouldFetchRating(getState().feedback.get('rating'))) {
    return null;
  }

  return dispatch(fetchRating(contactId));
};

// SELECTORS
const feedbackSelector = state => state.feedback;

export const getCreateFeedbackLoading = createSelector([feedbackSelector], feedback => feedback.get('createLoading'));

export const getCreateFeedbackError = createSelector([feedbackSelector], feedback => feedback.get('createError'));

export const getCreateFeedbackSuccess = createSelector([feedbackSelector], feedback => feedback.get('createSuccess'));

export const getCreateSupportCaseSuccess = createSelector([feedbackSelector], feedback => feedback.get('supportCase').get('createSuccess'));

export const getCreateSupportCaseLoading = createSelector([feedbackSelector], feedback => feedback.get('supportCase').get('createLoading'));

export const getCreateSupportCaseError = createSelector([feedbackSelector], feedback => feedback.get('supportCase').get('createError'));

export const getRating = createSelector([feedbackSelector], feedback => feedback.get('rating').get('number'));

export const getRatingLoading = createSelector([feedbackSelector], feedback => feedback.get('rating').get('loading'));

export const getRatingError = createSelector([feedbackSelector], feedback => feedback.get('rating').get('error'));

export const getCreateRatingError = createSelector([feedbackSelector], feedback => feedback.get('rating').get('createError'));

// REDUCER
export const initialState = fromJS({
  createLoading: false,
  createError: null,
  createSuccess: false,
  rating: {
    number: null,
    loaded: false,
    loading: false,
    error: null,
    createLoading: false,
    createError: null,
    createSuccess: false,
  },
  supportCase: {
    createLoading: false,
    createError: null,
    createSuccess: false,
  },
});

export default (state = initialState, action) => {
  switch (action.type) {
    case CREATE_FEEDBACK_SUCCESS:
      return state.set('createLoading', false).set('createSuccess', true);
    case CREATE_FEEDBACK_ERROR:
      return state.set('createError', action.payload).set('createLoading', false);
    case CREATING_FEEDBACK:
      return state.set('createError', null).set('createLoading', true);
    case CREATING_SUPPORT_CASE:
      return state.setIn(['supportCase', 'createLoading'], true).setIn(['supportCase', 'createError'], null);
    case CREATE_SUPPORT_CASE_SUCCESS:
      return state.setIn(['supportCase', 'createSuccess'], true).setIn(['supportCase', 'createLoading'], false);
    case CREATE_SUPPORT_CASE_ERROR:
      return state.setIn(['supportCase', 'createError'], action.payload).setIn(['supportCase', 'createLoading'], false);
    case CREATING_RATING:
      return state.setIn(['rating', 'createLoading'], true).setIn(['rating', 'createError'], null);
    case CREATE_RATING_SUCCESS:
      return state.setIn(['rating', 'createSuccess'], true).setIn(['rating', 'number'], action.payload).setIn(['rating', 'createLoading'], false);
    case CREATE_RATING_ERROR:
      return state.setIn(['rating', 'createError'], action.payload).setIn(['rating', 'createLoading'], false);
    case FETCHING_RATING:
      return state.setIn(['rating', 'loading'], true).setIn(['rating', 'loaded'], false).setIn(['rating', 'error'], null);
    case FETCH_RATING_SUCCESS:
      return state.setIn(['rating', 'number'], action.payload).setIn(['rating', 'loading'], false).setIn(['rating', 'loaded'], true);
    case FETCH_RATING_ERROR:
      return state.setIn(['rating', 'loading'], false).setIn(['rating', 'error'], action.payload);
    default:
      return state;
  }
};
